import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, BadgeCheck } from 'lucide-react';
import { useTrucks } from '../hooks/useTrucks';
import { SoldRibbon } from './SoldRibbon';

/**
 * Recently Sold horizontal carousel — social proof for the homepage.
 * Pulls the latest sold vehicles and renders a scroll-snap row with manual
 * prev / next controls.
 */
const RecentlySoldCarousel = () => {
  const { trucks: soldTrucks, loading } = useTrucks({ is_sold: true, limit: 12 });
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8 * (dir === 'next' ? 1 : -1);
    el.scrollBy({ left: amount, behavior: 'smooth' });
  };

  if (loading) return null;
  if (!soldTrucks || soldTrucks.length === 0) return null;

  return (
    <section
      className="py-16 bg-black text-white relative overflow-hidden"
      data-testid="recently-sold-section"
    >
      {/* Subtle orange grain */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #f97316 0%, transparent 50%), radial-gradient(circle at 80% 70%, #f97316 0%, transparent 50%)' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs font-bold uppercase tracking-widest mb-3">
              <BadgeCheck className="h-3.5 w-3.5" />
              Social Proof
            </div>
            <h2 className="text-3xl lg:text-4xl font-black uppercase font-inter">
              Recently <span className="text-orange-500">Sold</span>
            </h2>
            <p
              className="text-lg lg:text-xl italic text-gray-300 mt-3"
              style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 500 }}
            >
              Vehicles that have already found new homes
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => scrollBy('prev')}
              data-testid="recently-sold-prev"
              aria-label="Previous"
              className="w-11 h-11 rounded-full border border-white/20 bg-white/5 hover:bg-orange-500 hover:border-orange-500 transition-colors flex items-center justify-center"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy('next')}
              data-testid="recently-sold-next"
              aria-label="Next"
              className="w-11 h-11 rounded-full border border-white/20 bg-white/5 hover:bg-orange-500 hover:border-orange-500 transition-colors flex items-center justify-center"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4 scrollbar-hide"
          style={{ scrollbarWidth: 'none' }}
          data-testid="recently-sold-scroller"
        >
          {soldTrucks.map((truck) => (
            <Link
              key={truck.id}
              to={`/trucks/${truck.id}`}
              className="snap-start flex-shrink-0 w-72 md:w-80 group"
              data-testid={`sold-card-${truck.id}`}
            >
              <div className="relative rounded-xl overflow-hidden bg-gray-900 border border-white/10 transition-all duration-300 group-hover:border-orange-500/50 group-hover:-translate-y-1">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={truck.images && truck.images.length > 0 ? truck.images[0] : 'https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png'}
                    alt={truck.title}
                    className="w-full h-full object-cover grayscale opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                  />
                  <SoldRibbon size="md" />
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black via-black/60 to-transparent" />
                </div>

                <div className="p-5">
                  <h3 className="text-base font-bold mb-2 line-clamp-2 font-inter group-hover:text-orange-400 transition-colors">
                    {truck.title}
                  </h3>
                  <div className="flex items-center justify-between text-sm text-gray-400">
                    <span>{truck.year || ''}</span>
                    <span className="text-orange-500 font-bold tracking-wider uppercase text-xs">
                      Sold
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8 text-center">
          <p className="text-gray-400 font-inter text-sm">
            Looking for something similar?{' '}
            <Link to="/trucks" className="text-orange-500 font-bold hover:text-orange-400">
              Browse our available stock
            </Link>{' '}
            or{' '}
            <a href="tel:01530231235" className="text-orange-500 font-bold hover:text-orange-400">
              call 01530 231 235
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  );
};

export default RecentlySoldCarousel;
